import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Plus } from 'lucide-react';
import InnerHeader from '../components/common/InnerHeader/InnerHeader';
import InnerFooter from '../components/common/InnerFooter/InnerFooter';
import AssetsSidebar from '../components/common/AssetsSidebar/AssetsSidebar';
import './Dashboard.css';
import './Assets.css';
import './AssetsDashboard.css';
import './styles/pages-common.css';



export const AssetsDashboard = ({ setView }) => {
    const [user, setUser] = useState(() => {
        try {
            const stored = localStorage.getItem('user');
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    });
    const [dashboardData, setDashboardData] = useState(null);
    const [alert, setAlert] = useState(null);
    const [activeTopNav, setActiveTopNav] = useState('Asset');

    // ── Fetch dashboard counts ──────────────────────────────────────────────────
    const fetchDashBoardData = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) return null;

            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/dashboard-counts/`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) throw new Error('Failed to fetch dashboard data');

            const result = await response.json();
            // console.log('Dashboard API Response:', result);

            if (result.status && result.data) {
                setDashboardData(result.data);
                return result.data;
            } else {
                throw new Error('Invalid dashboard response');
            }
        } catch (error) {
            console.error('Error loading dashboard data:', error);
            return null;
        }
    };

    // ── Fetch user ──────────────────────────────────────────────────────────────
    const fetchUserDetails = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) return null;
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user/AlluserDetails/`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
            });
            if (!response.ok) throw new Error('Failed to fetch user details');
            const data = await response.json();
            if (data.status && data.data?.user) {
                const userData = data.data.user;
                if (userData.profile_picture && !userData.picture) userData.picture = userData.profile_picture;
                setUser(userData);
                localStorage.setItem('user', JSON.stringify(userData));
                return userData;
            }
            throw new Error('Invalid user data');
        } catch {
            const stored = localStorage.getItem('user');
            if (stored) { const u = JSON.parse(stored); setUser(u); return u; }
            return null;
        }
    };

    useEffect(() => {
        let isMounted = true;

        fetchUserDetails().then(userData => {
            if (!userData && isMounted) {
                setAlert({ type: 'error', message: 'Error loading user information' });
                setTimeout(() => setView && setView('signin'), 500);
            }
        });

        // Fetch counts in the background
        fetchDashBoardData();

        return () => {
            isMounted = false;
        };
    }, [setView]);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="assets-root"
        >

            {/* ══ 1. MAIN HEADER (shared component) ══════════════════════════════ */}
            <InnerHeader
                user={user}
                setView={setView}
                onProfilePhotoSuccess={async () => {
                    setTimeout(async () => { await fetchUserDetails(); }, 500);
                    setAlert({ type: 'success', message: 'Profile picture updated successfully!' });
                    setTimeout(() => setAlert(null), 3000);
                }}
            />

            {/* ══ 2. SECONDARY BAR: "+ ASSETS" title + page nav ══════════════════ */}
            <div className="assets-secondary-bar">
                <div className="assets-title-wrap">
                    {/* <div className="assets-plus-icon"><Plus size={13} /></div> */}
                    <span className="assets-page-title">ASSETS</span>
                </div>

                <div className="assets-top-nav">
                    {/* Navigation buttons commented out as requested - preserved for future use
                    {['Home', 'Dashboard', 'Analytics', 'Export Reports', 'Asset', 'Supplier', 'Pole Design', 'Maintenance']
                        .filter(label => {
                            if (label === 'Home') return true;
                            const role = user?.role;
                            const normRole = role ? role.replace(/[-_ ]/g, '').toLowerCase() : '';
                            if (label === 'Analytics' && normRole !== 'superadmin') return false;
                            if (normRole === 'technicalsupport') return true;
                            if (normRole === 'superadmin') return true;
                            if (normRole === 'government') return label === 'Dashboard';
                            if (normRole === 'maintenance' || normRole === 'research') {
                                return label === 'Dashboard' || label === 'Maintenance';
                            }
                            if (normRole === 'assetmanagement') {
                                return label !== 'Maintenance' && label !== 'Export Reports';
                            }
                            return true;
                        })
                        .map(label => (
                        <button
                            key={label}
                                className={`assets-top-nav-btn${activeTopNav === label ? ' active' : ''}`}
                                disabled={label === 'Export Reports'}
                                style={label === 'Export Reports' ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                            onClick={() => {
                                setActiveTopNav(label);
                                if (label === 'Home') setView && setView('home');
                                else if (label === 'Dashboard') setView && setView('dashboard');
                                else if (label === 'Analytics') setView && setView('analytics');
                                else if (label === 'Asset') setView && setView('assets-list');
                                else if (label === 'Supplier') setView && setView('supplier-list');
                                else if (label === 'Pole Design') setView && setView('pole-designs');
                                else if (label === 'Maintenance') setView && setView('maintenance');
                            }}
                        >
                            {label}
                        </button>
                    ))}
                    */}
                    <button
                        className="assets-top-nav-btn"
                        onClick={() => setView && setView('home')}
                        title="Back to Home"
                    >
                        ← Home
                    </button>
                </div>
            </div>

            {/* ══ 3. BODY ════════════════════════════════════════════════════════  */}
            <div className="assets-body">

                {/* Sidebar */}
                <AssetsSidebar setView={setView} activeItem="dashboard" />

                {/* Main content */}
                <div className="assets-main">

                    {/* Alert */}
                    {alert && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`assets-alert ${alert.type}`}
                        >
                            <CheckCircle size={18} />
                            <span className="alert-message-flex">{alert.message}</span>
                            <button className="assets-alert-close" onClick={() => setAlert(null)}>×</button>
                        </motion.div>
                    )}

                    {/* Dashboard KPI Card */}
                    <div className="assets-card asd-card-no-pad">

                        {/* Card header */}
                        <div className="asd-card-header">
                            Dashboard
                        </div>

                        {/* KPI Grid */}
                        <div className="asd-kpi-grid">
                            {[
                                { label: 'Total Sites', value: dashboardData ? (dashboardData.total_sites || 0) : '...' },
                                { label: 'Total Locations', value: dashboardData ? (dashboardData.total_locations || 0) : '...' },
                                { label: 'Total Categories', value: dashboardData ? (dashboardData.total_categories || 0) : '...' },
                                { label: 'Total Poles', value: dashboardData ? (dashboardData.total_poles || 0) : '...' },
                            ].map((card) => (
                                <div
                                    key={card.label}
                                    className="asd-kpi-card"
                                >
                                    <div className="asd-kpi-value">
                                        {card.value}
                                    </div>
                                    <div className="asd-kpi-label">
                                        {card.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            </div>

            {/* ══ 4. FOOTER (shared component) ═══════════════════════════════════ */}
            <InnerFooter />

        </motion.div>
    );
};

export default AssetsDashboard;
